<script setup lang="ts">
import { useFormValidation } from '../../../../composables/useFormValidation'
import { required } from '../../../../utils/validation'
import TextInput from '../../../../components/ui/form/TextInput.vue'
import CheckInput from '../../../../components/ui/form/CheckInput.vue'
import { api } from '../../../../api/Api'
import Toast from '../../../../services/Toast'
import { Permission } from '../../../../types/auth'
import SearchSelect from '../../../../components/ui/form/SearchSelect.vue'
import FileUpload from '../../../../components/ui/form/FileUpload.vue'
import { ref, onMounted } from 'vue'
import ModalFormTemplate from '../../../../components/ui/Templates/ModalFormTemplate.vue'
import { TableField } from '../../../../components/table/table'
import AddDeminations from './AddDeminations.vue'
import DataTable from '../../../../components/table/DataTable.vue'
import Buttons from '../../../../components/ui/form/Buttons.vue'
import NumberInput from '../../../../components/ui/form/NumberInput.vue'
import ColorInput from '../../../../components/ui/form/ColorInput.vue'
import AmountInput from '../../../../components/ui/form/AmountInput.vue'

const props = defineProps<{
  data?: any | null
  mode: 'create' | 'edit' | 'view' | 'showQuickView' | 'toggleStatus'
  title: string | null
  endPoint: string,
  permissions?: Permission[],
  listCreateName?: string
}>()

const emit = defineEmits<{
  (e: 'save', data: any): void
  (e: 'close'): void
}>()

const { fields, formData, setFieldRules, validateForm, resetForm, setFormData } = useFormValidation({
    id: props.data?.id || "",
    code: props.data?.code || "",
    name: props.data?.name || props.listCreateName || "",
    icon_image: props.data?.icon_image || "",
    sort_order: props.data?.sort_order || 0,
    allow_over_tender: props.data?.allow_over_tender || false,
    allow_return: props.data?.allow_return || false,
    require_attachment: props.data?.require_attachment || false,
    require_customer: props.data?.require_customer || false,
    check_credit: props.data?.check_credit || false,
    require_verification: props.data?.require_verification || false,
    has_denomination: props.data?.has_denomination || false,
    issue_balance: props.data?.issue_balance || false,
    bank_charges_percent: props.data?.bank_charges_percent || "",
    chart_of_accounts_id: props.data?.chart_of_accounts_id || "",
    require_account: props.data?.require_account || false,
    require_payment_for_delivery: props.data?.require_payment_for_delivery || false,
    is_system: props.data?.is_system || false,
    back_color: props.data?.back_color || "",
    text_color: props.data?.text_color || "",
    require_reference: props.data?.require_reference || false,
    auto_generate_reference: props.data?.auto_generate_reference || false,
    read_ocr: props.data?.read_ocr || false,
    ocr_init_string: props.data?.ocr_init_string || "",
    ocr_string_length: props.data?.ocr_string_length || "",
    shortcut: props.data?.shortcut || "",
    active: props.data?.active || true,
    uid: props.data?.uid || "",
    denominations: [],
  });

setFieldRules("code", [required("Code is required")]);
setFieldRules("name", [required("Payment Type name is required")]);


const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != '' ? 'update' : 'create'
    const response = await api.post((props.endPoint.slice(-1) == '/' ? props.endPoint : props.endPoint + '/') + action, formData.value)
    if (response.success == true) {
      Toast.show('saved successfully')
      emit('save', response.data)
      if (close) {
        emit('close')
      } else if (newRecord) {
        resetForm()
      } else {
        setFormData(response.data)
      }
    }
  }
}

const handleGetRecord = async () => {
  if (!props.data)
    return
  const params = {
    id: props.data?.uid
  }
  const response = await api.post((props.endPoint.slice(-1) == '/' ? props.endPoint : props.endPoint + '/') + "record", params);
    if (response.success == true) {
      setFormData(response.data);
    }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      handleGetRecord()
    }
})


const showDenominationDialog = ref(false);
const selectedData = ref(null);

const tableFields: TableField[] = [
  { name: "name", caption: "Denomination", sortable: false, fieldType: "text" },
  {
    name: "multiply",
    caption: "Multiply",
    sortable: true,
    fieldType: "number",
  },
  {
    name: "sort_order",
    caption: "Sort Order",
    sortable: true,
    fieldType: "number",
  },
  { name: "active", caption: "active", sortable: false, fieldType: "status" },
];

</script>
<template>
  <ModalFormTemplate :data="data" :mode="mode" :title="title" :endPoint="endPoint" :perm="permissions"
    :listCreateName="listCreateName" :tabList="[]" :formData="formData" @save="handleSubmit"
    @close="$emit('close')">
    <template #form>
      <div id="document-section">
        <FileUpload
          accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
          label="Icon Image"
          :max-size="5"
          :multiple="false"
          :readonly="isReadOnly"
          :image="true"
          path="currency"
          v-model="fields.icon_image.value"
        />
      </div>
      <!-- Basic Information -->
      <div id="basic-info-section" class="grid grid-cols-2 gap-6">
        <TextInput
          v-model="fields.code.value"
          label="Code"
          placeholder="Enter Payment Type Code"
          :readonly="isReadOnly"
          :error="fields.code.touched ? fields.code.error : null"
          @blur="fields.code.touched = true"
        />

        <TextInput
          v-model="fields.name.value"
          label="Name"
          placeholder="Enter Payment Type Name"
          :readonly="isReadOnly"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />
      </div>
      <div id="" class="grid md:grid-cols-3 gap-6">
        <NumberInput
          v-model="fields.sort_order.value"
          label="Sort Order"
          :min="0"
          :readonly="isReadOnly"
          :error="fields.sort_order.touched ? fields.sort_order.error : null"
          @blur="fields.sort_order.touched = true"
        />

        <CheckInput
          v-model="fields.allow_over_tender.value"
          :readonly="isReadOnly"
          label="Allow Tender"
          :error="
            fields.allow_over_tender.touched
              ? fields.allow_over_tender.error
              : null
          "
          @blur="fields.allow_over_tender.touched = true"
        />
        <CheckInput
          v-model="fields.require_payment_for_delivery.value"
          :readonly="isReadOnly"
          label="Payment Before Delivery"
          :error="
            fields.require_payment_for_delivery.touched
              ? fields.require_payment_for_delivery.error
              : null
          "
          @blur="fields.require_payment_for_delivery.touched = true"
        />
        <CheckInput
          v-model="fields.allow_return.value"
          :readonly="isReadOnly"
          label="Allow Return"
          :error="
            fields.allow_return.touched ? fields.allow_return.error : null
          "
          @blur="fields.allow_return.touched = true"
        />
        <CheckInput
          v-model="fields.require_attachment.value"
          :readonly="isReadOnly"
          label="Require Attachment"
          :error="
            fields.require_attachment.touched
              ? fields.require_attachment.error
              : null
          "
          @blur="fields.require_attachment.touched = true"
        />
        <CheckInput
          v-model="fields.require_customer.value"
          :readonly="isReadOnly"
          label="Requrie Customer"
          :error="
            fields.require_customer.touched
              ? fields.require_customer.error
              : null
          "
          @blur="fields.require_customer.touched = true"
        />
        <CheckInput
          v-model="fields.check_credit.value"
          :readonly="isReadOnly"
          label="Check Credit"
          :error="
            fields.check_credit.touched ? fields.check_credit.error : null
          "
          @blur="fields.check_credit.touched = true"
        />
        <CheckInput
          v-model="fields.require_verification.value"
          :readonly="isReadOnly"
          label="Require Verification"
          :error="
            fields.require_verification.touched
              ? fields.require_verification.error
              : null
          "
          @blur="fields.require_verification.touched = true"
        />
        <CheckInput
          v-model="fields.has_denomination.value"
          :readonly="isReadOnly"
          label="Has Denomination"
          :error="
            fields.has_denomination.touched
              ? fields.has_denomination.error
              : null
          "
          @blur="fields.has_denomination.touched = true"
        />
        <CheckInput
          v-model="fields.issue_balance.value"
          :readonly="isReadOnly"
          label="Issue Balance"
          :class="{'col-span-3' : fields.require_account.value == true}"
          :error="
            fields.issue_balance.touched ? fields.issue_balance.error : null
          "
          @blur="fields.issue_balance.touched = true"
        />
        <CheckInput
          v-model="fields.require_account.value"
          :readonly="isReadOnly"
          label="Require Account"
          :error="
            fields.require_account.touched ? fields.require_account.error : null
          "
          @blur="fields.require_account.touched = true"
        />

        <SearchSelect
          label="Bank/Cash Account"
          end-point="finance/accounting/chart-of-accounts"
          v-model="fields.chart_of_accounts_id.value"
          v-if="fields.require_account.value == true"
          extra1="Current Assets"
          :readonly="isReadOnly"
          :remote="true"
          :withDetails="true"
          :error="
            fields.chart_of_accounts_id.touched
              ? fields.chart_of_accounts_id.error
              : null
          "
          @blur="fields.chart_of_accounts_id.touched = true"
        />

        <AmountInput
          v-model="fields.bank_charges_percent.value"
          label="Bank Charges (%)"
          :min="0"
          v-if="fields.require_account.value == true"
          :readonly="isReadOnly"
          :error="
            fields.bank_charges_percent.touched
              ? fields.bank_charges_percent.error
              : null
          "
          @blur="fields.bank_charges_percent.touched = true"
        />
      </div>
      <div id="" class="grid md:grid-cols-3 gap-6">
        <ColorInput
          v-model="fields.back_color.value"
          :readonly="isReadOnly"
          label="Back Color"
          :error="fields.back_color.touched ? fields.back_color.error : null"
          @blur="fields.back_color.touched = true"
        />
        <ColorInput
          v-model="fields.text_color.value"
          :readonly="isReadOnly"
          label="Text Color"
          :error="fields.text_color.touched ? fields.text_color.error : null"
          @blur="fields.text_color.touched = true"
        />

        <CheckInput
          v-model="fields.is_system.value"
          :readonly="true"
          label="Is System"
          :error="fields.is_system.touched ? fields.is_system.error : null"
          @blur="fields.is_system.touched = true"
        />

        <CheckInput
          v-model="fields.require_reference.value"
          :readonly="isReadOnly"
          label="Requrie Reference"
          :error="
            fields.require_reference.touched
              ? fields.require_reference.error
              : null
          "
          @blur="fields.require_reference.touched = true"
        />
        <CheckInput
          v-model="fields.auto_generate_reference.value"
          :readonly="isReadOnly"
          label="Auto Gen Ref."
          :error="
            fields.auto_generate_reference.touched
              ? fields.auto_generate_reference.error
              : null
          "
          @blur="fields.auto_generate_reference.touched = true"
        />
        <CheckInput
          v-model="fields.read_ocr.value"
          :readonly="isReadOnly"
          label="Read OCR"
          :error="fields.read_ocr.touched ? fields.read_ocr.error : null"
          @blur="fields.read_ocr.touched = true"
        />

        <TextInput
          v-model="fields.ocr_init_string.value"
          :readonly="isReadOnly"
          label="OCR Init String"
          :error="
            fields.ocr_init_string.touched ? fields.ocr_init_string.error : null
          "
          @blur="fields.ocr_init_string.touched = true"
        />

        <NumberInput
          v-model="fields.ocr_string_length.value"
          :readonly="isReadOnly"
          label="OCR Text Length"
          :min="1"
          :error="
            fields.ocr_string_length.touched
              ? fields.ocr_string_length.error
              : null
          "
          @blur="fields.ocr_string_length.touched = true"
        />
        <TextInput
          v-model="fields.shortcut.value"
          :readonly="isReadOnly"
          label="Shortcut"
          :error="fields.shortcut.touched ? fields.shortcut.error : null"
          @blur="fields.shortcut.touched = true"
        />
      </div>
      <div class="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
        <div class="">
          <span class="text-2xl font-medium text-gray-900 dark:text-white"
            >Denominations</span
          >
          <Buttons
            id="form-add-denomination"
            class="float-right"
            @click="showDenominationDialog = true"
            caption="Add Denomination"
            icon="FilePlus"
          />
          <div>
            <DataTable
              :fields="tableFields"
              :permissions="permissions"
              :rows="fields.denominations.value"
              :title="title"
              :allow-disable="true"
              :actions="[]"
              :endPoint="endPoint + 'denomination-'"
              :loadData="false"
              :show-header="false"
              :show-pagination="false"
            />
          </div>
        </div>
      </div>
      <AddDeminations
        v-if="showDenominationDialog"
        :data="selectedData"
        :permissions="permissions"
        :parent="formData"
        title="Add Denomination"
        @save="handleGetRecord"
        :endPoint="endPoint"
        mode="create"
        @close="showDenominationDialog = false"
      />
    </template>
  </ModalFormTemplate>
</template>